import { useCallback, useEffect, useMemo, useState } from 'react'

import { getKpiGoals } from '@/api/kpi'
import { ClarityKpiGoalResponseModel, KpiGoalsModel } from '@/types'

// Define the fields for each group based on KpiGoalsModel
const KPI_GROUP_FIELDS: Record<keyof KpiGoalsModel, string[]> = {
  dashboard: ['googleReviews'],
  sales: ['adns', 'traffic', 'aov', 'discountPercentage'],
  spend: [
    'cogsPercentage',
    'ptiImpact',
    'compliancePercentage',
    'cogsVsTheoretical',
    'inventoryValue',
    'totalSpend',
  ],
  labor: [
    'laborRatio',
    'laborHours',
    'wagePerHour',
    'tipPercentage',
    'overtimePercentage',
  ],
  speedOfService: [
    'speedOfService',
    'utilization',
    'serviceTime',
    'windowTime',
  ],
  loyalty: [
    'loyaltyPercentage',
    'newUserPercentage',
    'frequency',
    'retentionPercentage',
    'discountPenetrationPercentage',
  ],
}

const toSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

// Find value from response - tries camelCase and snake_case variations
const findValue = (
  response: ClarityKpiGoalResponseModel,
  fieldName: string
): number | null => {
  const snakeCaseField = toSnakeCase(fieldName)

  // Try exact match first (camelCase), then snake_case
  if (fieldName in response && response[fieldName] !== undefined) {
    return response[fieldName]
  }
  if (snakeCaseField in response && response[snakeCaseField] !== undefined) {
    return response[snakeCaseField]
  }

  return null
}

// Normalize group name for matching
const normalizeGroupName = (name: string): string =>
  name.toLowerCase().replace(/[_\s-]/g, '')

// Get the actual group key from KpiGoalsModel based on kpiGroupName
const getGroupKey = (kpiGroupName: string): keyof KpiGoalsModel | null => {
  const normalized = normalizeGroupName(kpiGroupName)
  const groupKeys = Object.keys(KPI_GROUP_FIELDS) as (keyof KpiGoalsModel)[]

  return groupKeys.find((key) => normalizeGroupName(key) === normalized) ?? null
}

// Maps API response keys to KpiGoalsModel structure based on kpiGroupName
const mapResponseToKpiGoals = (
  response: ClarityKpiGoalResponseModel,
  kpiGroupName: string
): KpiGoalsModel => {
  const groupKey = getGroupKey(kpiGroupName)

  if (!groupKey) {
    return {}
  }

  const fields = KPI_GROUP_FIELDS[groupKey]
  const groupData: Record<string, number | null | undefined> = {}

  for (const field of fields) {
    groupData[field] = findValue(response, field)
  }

  return {
    [groupKey]: groupData,
  } as KpiGoalsModel
}

export default function useKpiGoals(
  startDate: string,
  endDate: string,
  kpiGroupName: string,
  branches: string,
  tenant: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [kpiGoals, setKpiGoals] = useState<KpiGoalsModel | null>(null)

  const fetchData = useMemo(() => {
    return async () => {
      try {
        if (!kpiGroupName || !branches) {
          setKpiGoals(null)
          setError(new Error('KPI group name and branches are required'))
          return
        }

        let mappedKpiGoals: KpiGoalsModel | null = null

        if (tenant !== 'blankstreet') {
          const response = await getKpiGoals(
            startDate,
            endDate,
            kpiGroupName,
            branches
          )

          // Map response to KpiGoalsModel if it's a ClarityKpiGoalResponseModel
          if (response && !('branches' in response)) {
            mappedKpiGoals = mapResponseToKpiGoals(
              response as ClarityKpiGoalResponseModel,
              kpiGroupName
            )
          }
        }

        setKpiGoals(mappedKpiGoals)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), kpiGroupName, startDate, endDate])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  const refetch = useCallback(() => {
    console.log('Refetching KPI goals data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    kpiGoals,
    loading,
    error,
    refetch,
  }
}
